import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import api, { PaginatedResponse } from '@/lib/api'
import { Activity, LogIn, Plus, Edit3, UserCheck, DollarSign, RefreshCw } from 'lucide-react'
import { formatMyanmarDateTime } from '@/lib/dateUtils'

interface ActivityLog {
  id: number
  user_id: number
  username: string
  full_name: string
  action: string
  entity_type: string
  entity_id: number | null
  details: string
  created_at: string
}

const ACTION_OPTIONS = [
  { value: '', label: 'All Actions' },
  { value: 'login', label: 'Login' },
  { value: 'create', label: 'Create' },
  { value: 'edit', label: 'Edit' },
  { value: 'status_change', label: 'Status Change' },
  { value: 'owner_change', label: 'Owner Change' },
  { value: 'bill', label: 'Billing' },
]

const ENTITY_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'contact', label: 'Contact' },
  { value: 'deal', label: 'Deal' },
  { value: 'service_request', label: 'Service Request' },
  { value: 'site_survey', label: 'Site Survey' },
  { value: 'project', label: 'Project' },
]

function getActionIcon(action: string) {
  switch (action) {
    case 'login': return <LogIn size={14} className="text-blue-600" />
    case 'create': return <Plus size={14} className="text-green-600" />
    case 'edit': return <Edit3 size={14} className="text-amber-600" />
    case 'status_change': return <RefreshCw size={14} className="text-purple-600" />
    case 'owner_change': return <UserCheck size={14} className="text-teal-600" />
    case 'bill': return <DollarSign size={14} className="text-emerald-600" />
    default: return <Activity size={14} className="text-slate-400" />
  }
}

function getActionColor(action: string) {
  switch (action) {
    case 'login': return 'bg-blue-100 text-blue-700'
    case 'create': return 'bg-green-100 text-green-700'
    case 'edit': return 'bg-amber-100 text-amber-700'
    case 'status_change': return 'bg-purple-100 text-purple-700'
    case 'owner_change': return 'bg-teal-100 text-teal-700'
    case 'bill': return 'bg-emerald-100 text-emerald-700'
    default: return 'bg-slate-100 text-slate-600'
  }
}

function formatEntityType(type: string) {
  return type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || '—'
}

export default function ActivityLogs() {
  const [userFilter, setUserFilter] = useState('')
  const [action, setAction] = useState('')
  const [entityType, setEntityType] = useState('')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useQuery<PaginatedResponse<ActivityLog>>({
    queryKey: ['activity-logs', userFilter, action, entityType, page],
    queryFn: () =>
      api.get('/activity-logs', {
        params: { user_filter: userFilter, action, entity_type: entityType, page, per_page: 50 },
      }).then(r => r.data),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">User Activity Logs</h1>
        <span className="text-sm text-slate-500">{data?.total || 0} entries</span>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-3 border-b border-slate-200 flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Search by user..."
            value={userFilter}
            onChange={e => { setUserFilter(e.target.value); setPage(1) }}
            className="flex-1 min-w-[180px] px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-700"
          />
          <select value={action} onChange={e => { setAction(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            {ACTION_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={entityType} onChange={e => { setEntityType(e.target.value); setPage(1) }}
            className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg">
            {ENTITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-800"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Time</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">User</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Action</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Type</th>
                  <th className="text-left py-2 px-3 font-medium text-slate-600">Details</th>
                </tr>
              </thead>
              <tbody>
                {(data?.data || []).map(log => (
                  <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 px-3 text-xs text-slate-500 whitespace-nowrap">
                      {formatMyanmarDateTime(log.created_at)}
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-red-800 flex items-center justify-center text-[10px] font-semibold text-white shrink-0">
                          {log.full_name?.charAt(0) || log.username?.charAt(0) || '?'}
                        </div>
                        <span className="font-medium text-slate-800 truncate">{log.full_name || log.username}</span>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${getActionColor(log.action)}`}>
                        {getActionIcon(log.action)}
                        {log.action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-500">{formatEntityType(log.entity_type)}</td>
                    <td className="py-2 px-3 text-xs text-slate-600 max-w-[300px] truncate">{log.details}</td>
                  </tr>
                ))}
                {(data?.data || []).length === 0 && (
                  <tr><td colSpan={5} className="text-center py-8 text-slate-400">No activity logs found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
            <span className="text-xs text-slate-500">Page {data.page} of {data.total_pages}</span>
            <div className="flex gap-1">
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                className="px-3 py-1 text-xs border rounded disabled:opacity-40">Prev</button>
              <button disabled={page >= data.total_pages} onClick={() => setPage(p => p + 1)}
                className="px-3 py-1 text-xs border rounded disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
